import React, { useCallback, useEffect, useState } from "react";
import { FileDown, Plus, Gauge } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Field, Empty, ago, pdfUrl, CONGESTION_STYLE } from "./common";

const LEVELS = ["Normal", "Moderate", "High", "Critical", "Unknown"];

export default function PortIntelligence() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [statusRow, setStatusRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ port_name: "", country: "", unlocode: "", region: "", latitude: "", longitude: "" });
  const canEdit = hasPerm(user, "sea_freight", "create");
  const canCreate = hasPerm(user, "sea_freight", "create");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/sea-freight/ports", { params: { q, country, limit: 300 } });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [q, country]);
  useEffect(() => { load(); }, [load]);

  const saveStatus = async () => {
    try {
      await api.post(`/sea-freight/ports/${statusRow.id}/status`, statusRow);
      toast.success("Port status updated");
      setStatusRow(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addPort = async () => {
    try {
      await api.post("/sea-freight/ports", form);
      toast.success("Port added");
      setAddOpen(false); setForm({ port_name: "", country: "", unlocode: "", region: "", latitude: "", longitude: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="port-intelligence-page">
      <PageHead title="Port Intelligence"
        subtitle="Worldwide port database with UN/LOCODE, coordinates and operational status. Congestion is shown only where data has actually been recorded — never estimated."
        right={<>
          <Button data-testid="export-ports-pdf" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(pdfUrl("/sea-freight/export/ports.pdf", { country }), "_blank")}>
            <FileDown className="w-4 h-4" /> Congestion report
          </Button>
          {canCreate && <Button data-testid="add-port-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Add port</Button>}
        </>} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Input data-testid="port-search" placeholder="Search port, country, UN/LOCODE or port code" value={q} onChange={(e) => setQ(e.target.value)} className="bg-white h-9 sm:col-span-2" />
        <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="port-country" className="bg-white h-9"><SelectValue placeholder="All countries" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All countries</SelectItem>
            {(data?.countries || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data && data.ports.length === 0 ? <Empty testid="ports-empty">No port matched.</Empty> : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="ports-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{["Port", "Country", "UN/LOCODE", "Region", "Coordinates", "Operational", "Congestion", "Waiting (hrs)", "Avg wait", "Disruptions", "Updated / source", ""].map((h) => (
                <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody>
              {(data?.ports || []).map((p) => (
                <tr key={p.id} data-testid={`port-row-${p.unlocode}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2.5 text-slate-800 whitespace-nowrap">{p.port_name}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{p.country}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{p.unlocode}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{p.region}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{p.latitude != null ? `${p.latitude}, ${p.longitude}` : "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{p.operational_status}</td>
                  <td className="px-3 py-2.5"><Badge variant="outline" className={`rounded-full px-2 text-[11px] ${CONGESTION_STYLE[p.congestion_level]}`}>{p.congestion_level}</Badge></td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{p.vessel_waiting_time_hrs ?? "-"}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{p.avg_waiting_time_hrs ?? "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600 max-w-[220px] truncate">{p.disruptions || "-"}</td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-500">{p.status_updated_at ? ago(p.status_updated_at) : "no data"}<span className="block text-slate-400">{p.status_source}</span></td>
                  <td className="px-3 py-2.5 text-right">
                    {canEdit && <Button data-testid={`set-status-${p.unlocode}`} size="sm" variant="ghost" className="h-8 rounded-sm gap-1 text-slate-500 hover:text-[#0B132B]"
                      onClick={() => setStatusRow({ ...p, congestion_level: p.congestion_level || "Unknown" })}><Gauge className="w-3.5 h-3.5" /> Status</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!statusRow} onOpenChange={() => setStatusRow(null)}>
        <DialogContent className="max-w-lg" data-testid="port-status-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{statusRow?.port_name} — operational status</DialogTitle>
            <DialogDescription>Record what you actually know from the line, agent or port authority. This is stored with your name and timestamp as the source.</DialogDescription>
          </DialogHeader>
          {statusRow && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Congestion level">
                <Select value={statusRow.congestion_level} onValueChange={(v) => setStatusRow({ ...statusRow, congestion_level: v })}>
                  <SelectTrigger data-testid="status-congestion" className="bg-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Operational status">
                <Select value={statusRow.operational_status || "Operational"} onValueChange={(v) => setStatusRow({ ...statusRow, operational_status: v })}>
                  <SelectTrigger data-testid="status-operational" className="bg-white h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Operational", "Partially operational", "Closed", "Suspended"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Vessel waiting time (hrs)"><Input data-testid="status-wait" type="number" min="0" value={statusRow.vessel_waiting_time_hrs ?? ""} onChange={(e) => setStatusRow({ ...statusRow, vessel_waiting_time_hrs: e.target.value })} className="bg-white h-9" /></Field>
              <Field label="Average waiting time (hrs)"><Input data-testid="status-avgwait" type="number" min="0" value={statusRow.avg_waiting_time_hrs ?? ""} onChange={(e) => setStatusRow({ ...statusRow, avg_waiting_time_hrs: e.target.value })} className="bg-white h-9" /></Field>
              <div className="sm:col-span-2"><Field label="Disruptions / remarks"><Input data-testid="status-disruptions" value={statusRow.disruptions || ""} onChange={(e) => setStatusRow({ ...statusRow, disruptions: e.target.value })} className="bg-white h-9" /></Field></div>
              <div className="sm:col-span-2"><Field label="Data source"><Input data-testid="status-source" value={statusRow.source || ""} onChange={(e) => setStatusRow({ ...statusRow, source: e.target.value })} placeholder="e.g. Agent update 12-Jun / Port circular" className="bg-white h-9" /></Field></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setStatusRow(null)}>Cancel</Button>
            <Button data-testid="save-status-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={saveStatus}>Save status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-port-dialog">
          <DialogHeader><DialogTitle className="font-head">Add port</DialogTitle>
            <DialogDescription>Add a port that is not in the UN/LOCODE seed list.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Port name"><Input data-testid="new-port-name" value={form.port_name} onChange={(e) => setForm({ ...form, port_name: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Country"><Input data-testid="new-port-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="UN/LOCODE"><Input data-testid="new-port-unlocode" value={form.unlocode} onChange={(e) => setForm({ ...form, unlocode: e.target.value })} placeholder="e.g. INMUN" className="bg-white h-9" /></Field>
            <Field label="Region"><Input data-testid="new-port-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Latitude"><Input data-testid="new-port-lat" type="number" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Longitude"><Input data-testid="new-port-lon" type="number" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="bg-white h-9" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="save-port-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={addPort}>Add port</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
